import { RefObject, useEffect, useRef } from "react";
import { StoreApi } from "zustand";

import { InteractiveTableStoreType } from "./interactive_table.store";
import { DragAxis } from "./interactive_table.types";

const SCROLL_THRESHOLD = 60; // px from container edge
const MAX_SPEED = 12; // px per animation frame
const ACTIONS_CELL_WIDTH = 40;

//Scrolls `container` the minimum amount so that `cell` is fully visible
function scrollCellIntoView(cell: HTMLElement, container: HTMLElement): void {
  const cellRect = cell.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  const overflowRight = cellRect.right - containerRect.right + ACTIONS_CELL_WIDTH;
  const overflowLeft = containerRect.left - cellRect.left;
  const overflowBottom = cellRect.bottom - containerRect.bottom;
  const overflowTop = containerRect.top - cellRect.top;

  if (overflowRight > 0) {
    container.scrollLeft += overflowRight;
  } else if (overflowLeft > 0) {
    container.scrollLeft -= overflowLeft;
  }

  if (overflowBottom > 0) {
    container.scrollTop += overflowBottom;
  } else if (overflowTop > 0) {
    container.scrollTop -= overflowTop;
  }
}

/**
 * Returns the number of pixels to scroll along one axis given the mouse
 * position and the container's edge positions.
 * Positive = scroll forward (right/down), negative = scroll backward (left/up).
 * Returns 0 when the mouse is outside the threshold zone.
 */
function getScrollDelta(mousePos: number, edgeStart: number, edgeEnd: number): number {
  const distFromStart = mousePos - edgeStart;
  const distFromEnd = edgeEnd - mousePos;

  if (distFromStart < SCROLL_THRESHOLD) {
    return -Math.round(MAX_SPEED * (1 - distFromStart / SCROLL_THRESHOLD));
  }
  if (distFromEnd < SCROLL_THRESHOLD) {
    return Math.round(MAX_SPEED * (1 - distFromEnd / SCROLL_THRESHOLD));
  }
  return 0;
}

/**
 * Handles all auto-scroll behaviour for InteractiveTable:
 *
 * 1. **Selection-follow scroll**: Subscribes to the store and scrolls the
 *    active cell into view whenever `selectionRange.active` changes.
 *    Covers: arrow keys, Shift+Arrow, Tab/Shift+Tab.
 *
 * 2. **Drag edge-scroll**: While `isDragging` or `isFillDragging` is true,
 *    runs a rAF loop that continuously scrolls the container when the cursor
 *    is within SCROLL_THRESHOLD pixels of any container edge.
 *    Respects `dragAxis` — only scrolls the permitted axes.
 *
 * No-op when `scrollableContainerRef` is not provided or its current value is null.
 */
export function useAutoScroll(
  store: StoreApi<InteractiveTableStoreType>,
  scrollableContainerRef: RefObject<HTMLElement | null> | undefined,
  wrapperRef: RefObject<HTMLDivElement | null>,
  dragAxis: DragAxis
): void {
  const mousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const rafHandleRef = useRef<number | null>(null);
  const dragAxisRef = useRef(dragAxis);

  useEffect(() => {
    dragAxisRef.current = dragAxis;
  }, [dragAxis]);

  // --- Selection-follow scroll ---
  useEffect(() => {
    if (!scrollableContainerRef) {
      return;
    }

    let prevActive = store.getState().selectionRange?.active;

    const unsubscribe = store.subscribe((state) => {
      const container = scrollableContainerRef.current;
      if (!container) {
        return;
      }

      const active = state.selectionRange?.active;
      if (!active) {
        return;
      }

      if (prevActive && prevActive.rowIndex === active.rowIndex && prevActive.colIndex === active.colIndex) {
        return;
      }
      prevActive = active;

      const cell = wrapperRef.current?.querySelector<HTMLElement>(
        `td[data-row-index="${active.rowIndex}"][data-col-index="${active.colIndex}"]`
      );
      if (!cell) {
        return;
      } // cell may be virtualized out of DOM — safe to skip

      scrollCellIntoView(cell, container);
    });

    return unsubscribe;
  }, [store, scrollableContainerRef, wrapperRef]);

  // --- Drag edge-scroll ---
  useEffect(() => {
    if (!scrollableContainerRef) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      mousePosRef.current = { x: event.clientX, y: event.clientY };
    };

    document.addEventListener("mousemove", handleMouseMove);

    const tick = () => {
      const container = scrollableContainerRef.current;
      const { isDragging, isFillDragging } = store.getState();

      if (!isDragging && !isFillDragging) {
        rafHandleRef.current = null;
        return; // stop the loop
      }

      if (container) {
        const rect = container.getBoundingClientRect();
        const { x, y } = mousePosRef.current;

        if (dragAxisRef.current !== DragAxis.Vertical) {
          const dx = getScrollDelta(x, rect.left, rect.right);
          if (dx !== 0) {
            container.scrollLeft += dx;
          }
        }

        if (dragAxisRef.current !== DragAxis.Horizontal) {
          const dy = getScrollDelta(y, rect.top, rect.bottom);
          if (dy !== 0) {
            container.scrollTop += dy;
          }
        }
      }

      rafHandleRef.current = requestAnimationFrame(tick);
    };

    const unsubscribe = store.subscribe((state) => {
      const isDragging = state.isDragging || state.isFillDragging;
      if (isDragging && rafHandleRef.current === null) {
        rafHandleRef.current = requestAnimationFrame(tick);
      }
    });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      unsubscribe();
      if (rafHandleRef.current !== null) {
        cancelAnimationFrame(rafHandleRef.current);
        rafHandleRef.current = null;
      }
    };
  }, [store, scrollableContainerRef]);
}
